import { ProfileInsurance } from 'ui/components';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ProfileContext } from 'application/adapters/context/Profile';
import { fetchProfileInsurance, updatePolicyPermission } from 'application/repositories/profileInsuranceRepository';
import { PoliciesResult } from 'application/adapters/profileInsurance/profileInsuranceAdapter';
import { getCMSHostname } from 'helpers/getCMSHostname';

export const ProfileInsuranceFeature: React.FC<Umbraco.Profile> = ({ content }) => {
	const [insuranceDetails, setInsuranceDetails] = useState<PoliciesResult>({
		polices: [],
		permission: false,
		success: false,
	});
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const { profile } = useContext(ProfileContext);

	const fetchAndSetInsuranceDetails = useCallback(async () => {
		if (!profile) return;

		setIsLoading(true);
		try {
			const insurance = await fetchProfileInsurance(getCMSHostname(), profile.membership?.id);
			setInsuranceDetails(insurance);
		} catch (error) {
			console.error('Error fetching profile insurance:', error);
			setInsuranceDetails((prevState) => ({
				...prevState,
				polices: [],
				permission: false,
				success: false,
			}));
		} finally {
			setIsLoading(false);
		}
	}, [profile]);

	useEffect(() => {
		fetchAndSetInsuranceDetails();
	}, [fetchAndSetInsuranceDetails]);

	const changePermission = useCallback(async () => {
		if (!profile) return;

		setIsLoading(true);
		try {
			const permissionGranted = await updatePolicyPermission(getCMSHostname(), profile.membership?.id);
			await fetchAndSetInsuranceDetails();
			setInsuranceDetails((prevState) => ({
				...prevState,
				permission: permissionGranted,
			}));
		} catch (error) {
			console.error('Error changing policy permission:', error);
			setInsuranceDetails((prevState) => ({
				...prevState,
				polices: [],
				permission: false,
				success: false,
			}));
		} finally {
			setIsLoading(false);
		}
	}, [profile, fetchAndSetInsuranceDetails]);

	const insurance = profile?.insurance;
	//? NOTE: This is a workaround for the fact insurance is a string, but an array in the backend.
	//? This should be fixed in the backend, so it a list.
	const groupLifeInsuranceIsSubscribed = useMemo(() => {
		if (typeof insurance === 'string') {
			return insurance.split(',').includes('GR Gruppelivsforsikring');
		}
		return false;
	}, [insurance]);

	const fullTimeAccidentInsuranceIsSubscribed = useMemo(() => {
		if (typeof insurance === 'string') {
			return insurance.split(',').includes('GR Ulykkesforsikring') || insurance.split(',').includes('FR Fritidsulykkesforsikring');
		}
		return false;
	}, [insurance]);

	return (
		<ProfileInsurance
			profile={profile}
			otherInsurancesDetails={insuranceDetails.polices}
			groupLifeInsuranceIsSubscribed={groupLifeInsuranceIsSubscribed}
			fullTimeAccidentInsuranceIsSubscribed={fullTimeAccidentInsuranceIsSubscribed}
			otherInsurancesIsSubscribed={insuranceDetails.permission}
			otherInsurancesIsLoading={isLoading}
			otherInsurancesIsSuccess={insuranceDetails.success}
			{...content?.properties}
			otherInsurancesConsentBtnClick={changePermission}
		/>
	);
};
